import React, { useMemo, useState } from 'react';
import { Input, InputGroup, Loader, Message } from 'rsuite';

import PageHeader from '../components/shared/PageHeader';
import ProfilePhoto from '../components/employees/ProfilePhoto';
import { useCompanyDirectory } from '../hooks/useQueries';
import './CompanyDirectoryPage.css';

// Everyone in the company, as a card each.
//
// A lookup page rather than an admin one: it answers "who is this person and
// how do I reach them", and nothing on it can be changed. The roster comes from
// /employees/directory, which is readable by any signed-in employee - editing
// anybody's record still lives behind the admin pages.

const fullName = (employee) => (
  `${employee?.first_name || ''} ${employee?.last_name || ''}`.trim() || 'Unnamed'
);

const initialsOf = (employee) => {
  const first = String(employee?.first_name || '').trim().slice(0, 1);
  const last = String(employee?.last_name || '').trim().slice(0, 1);
  return `${first}${last}`.toUpperCase() || 'U';
};

export default function CompanyDirectoryPage() {
  const directoryQuery = useCompanyDirectory({ enabled: true });
  const [search, setSearch] = useState('');

  // Alphabetical by the name as it is printed on the card - first name, then
  // last to settle ties. The server returns the roster by surname, which reads
  // as unsorted here: the cards say "Kelton Orth", so an eye running down them
  // is following K, not O.
  const employees = useMemo(() => {
    const rows = directoryQuery.data || [];
    return [...rows].sort((a, b) => (
      String(a?.first_name || '').localeCompare(String(b?.first_name || ''), undefined, { sensitivity: 'base' })
      || String(a?.last_name || '').localeCompare(String(b?.last_name || ''), undefined, { sensitivity: 'base' })
    ));
  }, [directoryQuery.data]);

  // Name and email only. Job title is deliberately not on the card at the
  // moment, and searching a field nobody can see returns matches with no
  // visible reason for matching - so it is out of the haystack too. Both come
  // back together if the title does.
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return employees;
    return employees.filter((employee) => (
      `${fullName(employee)} ${employee.email || ''}`.toLowerCase().includes(term)
    ));
  }, [employees, search]);

  return (
    // .bdc-page is the shared page frame: measure, padding, and the 20px gap
    // that spaces a page's sections apart. Every page sits in one - without it
    // the header, the search and the grid stack flush against each other.
    <div className="bdc-page">
      <PageHeader
        crumbs={[{ label: 'Home', to: '/' }, { label: 'Directory' }]}
        icon="bi-people"
        title="Company Directory"
        count={directoryQuery.isLoading ? null : employees.length}
        note={search.trim() && !directoryQuery.isLoading
          ? `${filtered.length} of ${employees.length} shown`
          : null}
      />

      {directoryQuery.isError ? (
        <Message type="error" showIcon header="Could not load the directory">
          {directoryQuery.error?.message || 'Try again in a moment.'}
        </Message>
      ) : null}

      {/* Kept above the grid even while it loads, so the box does not appear
          under the cursor a moment after somebody has reached for it. */}
      <div className="directory-toolbar">
        <InputGroup inside className="directory-search">
          <Input
            placeholder="Search by name or email"
            value={search}
            onChange={setSearch}
          />
          <InputGroup.Addon>
            <i className="bi bi-search" aria-hidden="true" />
          </InputGroup.Addon>
        </InputGroup>
      </div>

      {directoryQuery.isLoading ? (
        <div className="directory-loading">
          <Loader size="md" content="Loading the directory&hellip;" vertical />
        </div>
      ) : filtered.length === 0 ? (
        <div className="directory-empty">
          <i className="bi bi-people" aria-hidden="true" />
          <h2>{employees.length === 0 ? 'Nobody to show' : 'No matches'}</h2>
          <p>
            {employees.length === 0
              ? 'The directory is empty. Active employees appear here automatically.'
              : `Nothing matches "${search.trim()}". Try a different name or email.`}
          </p>
        </div>
      ) : (
        <div className="directory-grid">
          {filtered.map((employee) => (
            <article className="directory-card" key={employee.id}>
              <ProfilePhoto
                employeeId={employee.id}
                size="medium"
                fallbackInitials={initialsOf(employee)}
                firstName={employee.first_name}
                lastName={employee.last_name}
              />

              <div className="directory-card__body">
                <h3 className="directory-card__name">{fullName(employee)}</h3>

                {/* A mailto rather than plain text: the email is the reason
                    most people open this page, so reading it and using it
                    should be the same action. */}
                {employee.email ? (
                  <a className="directory-card__email" href={`mailto:${employee.email}`}>
                    <i className="bi bi-envelope" aria-hidden="true" />
                    <span>{employee.email}</span>
                  </a>
                ) : (
                  <span className="directory-card__email directory-card__email--none">
                    <i className="bi bi-envelope" aria-hidden="true" />
                    <span>No email on file</span>
                  </span>
                )}
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  );
}
